import styled from 'styled-components';
import { Drawer } from 'antd';
import { getZIndexes, media } from 'utils/styledHelpers';
import { StyledButtonGrayIcon } from 'components/styledComponents/button/Button.styled';

interface StyledFilterWrapperProps {
  $isExpand?: boolean;
}

interface FormItemWrapperProps {
  $notFilterLabel?: boolean;
  $darkColor?: boolean;
  $midSize?: boolean;
}

const { mobileFilterFooterZIndex } = getZIndexes();

const FormItemWrapper = styled.div<FormItemWrapperProps>`
  .ant-form-item-required:after {
    color: ${(props) => props.theme.colors.filterLabelColor}!important;
  }

  .ant-picker-panel-container,
  .ant-picker-range-arrow::after {
    background: ${(props) => props.theme.colors.tableTrEvenColor};
  }
  & > span:first-child {
    display: block;
    color: ${(props) => props.theme.colors.secondaryColor};
    margin-bottom: 8px;
  }

  .ant-form-item .ant-form-item-label > label {
    color: ${(props) => (props.$notFilterLabel || props.$darkColor ? props.theme.colors.primaryText : props.theme.colors.filterLabelColor)};
  }
  .ant-form-item-label .ant-form-item-required:after {
    color: ${(props) => (props.$notFilterLabel || props.$darkColor ? props.theme.colors.primaryText : props.theme.colors.filterLabelColor)}!important;
  }
  .ant-picker.ant-picker-range,
  .ant-input {
    height: ${(props) => (props.$midSize ? '2.57rem' : '')};
  }
  .ant-picker-header {
    > button:hover {
      color: ${(props) => props.theme.colors.hover};
    }
  }
`;

const FilterButtonGroup = styled.div`
  display: flex;
  height: 100%;
  align-items: flex-end;

  ${media.sm`
    flex-wrap: wrap;
    row-gap: 0.71rem;
    padding: 0 0.87rem;
  `}

  > .ant-btn {
    width: auto;
    padding: 0 2.29rem;
    min-width: 7.15rem;
    font-size: 1rem;
    font-weight: 500;
    &:first-child {
      margin-right: 0.57rem;
    }
    &:last-child {
      > span {
        color: ${(props) => props.theme.colors.primaryText};
      }
    }
  }
`;

const StyledFilterWrapper = styled.div<StyledFilterWrapperProps>`
  display: ${(props) => (props.$isExpand ? 'block' : 'none')};
  margin-top: 1.71rem;
  padding-bottom: 1.71rem;

  & > .ant-btn {
    margin: 0 0 1.14rem auto;
  }

  .ant-form-item-label {
    > label {
      color: ${(props) => props.theme.colors.secondaryColor};
      &::after {
        display: none;
        content: '';
      }
    }
  }
  @media only screen and (min-width: 1921px) {
    .ant-form .ant-col:not(:last-of-type) {
      max-width: 320px;
    }
  }
`;

const StyledFilterDrawer = styled(Drawer)`
  .ant-drawer-header {
    color: ${(props) => props.theme.colors.primaryText};
    padding-top: 0.87rem;
    padding-bottom: 0.87rem;
  }
  .ant-drawer-body {
    padding-bottom: 80px;
    background: ${(props) => props.theme.colors.secondaryBg};
      
    .ant-form {
      & > .ant-row {
        & > .ant-col {
          width: 50%;
          flex: 0 0 50%;
          max-width: 50%;
            
          &:last-of-type {
            width: 100%;
            flex: 0 0 100%;
            max-width: 100%;
            position: absolute;
            bottom: 0;
            left: 0;
            z-index: ${mobileFilterFooterZIndex};
            transition: 0.3s all;
            padding: 0.58rem 24px !important;
            background-color: ${(props) => props.theme.colors.secondaryBg};
            box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);
          }
        }
      }
    }
  }
    
  ${media.xs`
    .ant-drawer-header {
      padding: 0.57rem 0 0.57rem 1rem;
      
      &-title {
        justify-content: flex-end;
      }
    }
    .ant-drawer-body {
      padding: 1rem 1rem 8rem;
      
      .ant-form {
        & > .ant-row {
          & > .ant-col {
            width: 100%;
            flex: 0 0 100%;
            max-width: 100%;
            
            &:last-of-type {
              padding: 1rem !important;
            }
          }
        }
      }
    }
  `};
}`;

const StyledFilterButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 1.28rem;
  row-gap: 0.52rem;

  ${media.sm`
    column-gap: 0.52rem;
    row-gap: 0.52rem;
    margin-bottom: 1.14rem;
  `}
  .ant-btn {
    margin: 0 0 0 1.14rem;
    ${media.sm`
      margin-bottom: 0;
    `}
  }
`;

const FilterIconWrapper = styled.div`
  & + span {
    margin-top: 0;
  }

  svg {
    margin-right: 0;
    path:not(g + path) {
      fill: ${(props) => props.theme.colors.secondaryColor};
    }
  }
`;

const StyledManageFiltersWrapper = styled.div`
  .ant-popover-container {
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
    width: 18.1rem;
    padding: 1rem 0;

    .ant-btn {
      margin: 1.71rem auto 0;
    }
  }

  ${StyledButtonGrayIcon} {
    margin-right: 1.14rem;

    &.ant-popover-open {
      background-color: ${(props) => props.theme.colors.grayButton};
    }
  }
`;

const StyledFilterButton = styled.div`
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${media.xs`
    flex-basis: 100%;
    justify-content: flex-end;
  `}

  .ant-btn:not(.ant-form .ant-btn, .ant-modal-footer .ant-btn) {
    padding-left: 0;
    min-width: 5rem;
    margin-bottom: 0 !important;
  }
  &:hover {
    span {
      color: ${(props) => props.theme.colors.blue};
    }
    svg {
      path {
        fill: ${(props) => props.theme.colors.blue};
      }
    }
  }
`;

export {
  FormItemWrapper, FilterButtonGroup, StyledFilterWrapper, StyledFilterButtons, FilterIconWrapper, StyledManageFiltersWrapper, StyledFilterDrawer, StyledFilterButton,
};
